import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  SpecialZoomLevel,
  Viewer,
  Worker,
  type PageLayout,
  type RenderPage,
  type RenderPageProps,
  type VisiblePagesRange,
} from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

import { useScrollDetectionPlugin } from '@hooks/useScrollDetectionPlugin';
import { TERMS_PDF_URL } from '@services/termsPdfService';
import {
  getPdfHeaderScale,
  TermsDialogActions,
  TermsDialogContent,
  TermsDialogTitle,
  TermsPdfPageOneHeader,
} from './TermsAndConditionsDialog.styles';

interface TermsAndConditionsDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
  /** Terms document to display; defaults to the Novus PDF. */
  pdfUrl?: string;
}

// termsAndConditions.pdf is US Letter (612pt × 792pt) with 1-inch (72pt) margins.
// MARGIN_RATIO = 72 / 792. If the PDF is redesigned, re-measure and update this value.
const PDF_MARGIN_RATIO = 72 / 792;

// CSS translateY % is relative to the element's own height, so this is zoom-independent.
const PDF_TOP_SHIFT = `-${(PDF_MARGIN_RATIO * 100).toFixed(4)}%`;

// Crop each page to its text content area, hiding PDF top/bottom white margins.
// Page 0 keeps its top margin so the PdfHeader overlay remains visible.
const pageLayout: PageLayout = {
  buildPageStyles: ({ pageIndex }) =>
    ({
      overflow: 'hidden',
      '--page-top-shift': pageIndex === 0 ? '0%' : PDF_TOP_SHIFT,
    }) as React.CSSProperties,
  transformSize: ({ pageIndex, size }) => ({
    width: size.width,
    height: pageIndex === 0
      ? size.height * (1 - PDF_MARGIN_RATIO)
      : size.height * (1 - 2 * PDF_MARGIN_RATIO),
  }),
};

const HEADER =
  'Review the terms and conditions to place the order. You must read through the entire document in order to accept the conditions.';

const PdfHeader = ({ scale }: { scale: number }) => (
  <TermsPdfPageOneHeader scale={scale}>
    <Typography variant="body.md">{HEADER}</Typography>
  </TermsPdfPageOneHeader>
);

// The dialog is nested inside an ancestor with its own overflow:auto scroll
// container (the host embed's page-level scroll wrapper), which the viewer's
// own visible-range detection doesn't track reliably — pages beyond the
// initial batch can stop rendering permanently after a large/fast scroll.
// Forcing every page into the render range sidesteps that instead of relying
// on the viewer to (re)detect visibility correctly.
const setRenderRange = ({ numPages }: VisiblePagesRange) => ({ startPage: 0, endPage: numPages - 1 });

const renderPage: RenderPage = (props: RenderPageProps) => (
  <>
    {props.canvasLayer.children}
    {props.pageIndex === 0 && <PdfHeader scale={getPdfHeaderScale(props.width)} />}
    {props.textLayer.children}
    {props.annotationLayer.children}
  </>
);

export const TermsAndConditionsDialog = ({
  open,
  onClose,
  onAccept,
  onDecline,
  pdfUrl = TERMS_PDF_URL,
}: TermsAndConditionsDialogProps) => {
  const { plugin: scrollPlugin, hasScrolledToBottom, reset: resetScroll } = useScrollDetectionPlugin();

  const acceptTooltip = hasScrolledToBottom
    ? ''
    : 'You must read and scroll through the entire document in order to accept the conditions.';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      variant="fullPageMobile"
      keepMounted
      TransitionProps={{ onEntering: resetScroll }}
    >
      <TermsDialogTitle>Terms and Conditions</TermsDialogTitle>
      <TermsDialogContent dividers>
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <Viewer
            fileUrl={pdfUrl}
            defaultScale={SpecialZoomLevel.PageWidth}
            plugins={[scrollPlugin]}
            renderPage={renderPage}
            pageLayout={pageLayout}
            setRenderRange={setRenderRange}
          />
        </Worker>
      </TermsDialogContent>
      <TermsDialogActions>
        <Button variant="termsDecline" onClick={onDecline}>
          DECLINE
        </Button>
        <Tooltip title={acceptTooltip} arrow placement="top">
          <span>
            <Button
              variant="contained"
              onClick={onAccept}
              disabled={!hasScrolledToBottom}
              sx={{ '&.Mui-disabled': { pointerEvents: 'auto' } }}
            >
              Accept
            </Button>
          </span>
        </Tooltip>
      </TermsDialogActions>
    </Dialog>
  );
};
